// transcript.ts — the offline STT pipeline, extracted from viewer.html so the
// portal (ingest-time transcription) and the viewer share one TESTED
// implementation (EGO-132 Phase B). Pure + deterministic: "code owns time,
// the LLM owns language" (docs/TRANSCRIPT_PIPELINE.md). Zero deps, runs in
// node and the browser identically.
//
// The one impure step — calling the Gemini worker for a chunk — is INJECTED
// (deps.transcribeChunk), so this module never fetches or encodes base64;
// the caller owns the network + its auth. That keeps the whole thing unit
// testable with a fake transcriber and keeps core dependency-free.

export interface TaskWindow {
  index: number;
  text: string;
  t_start_ns: number;
  t_end_ns: number;
  aborted?: boolean;
}

/** One STT segment as the worker returns it: seconds relative to the chunk. */
export interface RawSeg { start: number; end: number; text: string }

export type SubtaskTag = number | "intro" | "outro";

export interface TxSegment {
  start: number;                // seconds from audio start
  end: number;
  text: string;
  t_start_ns: number;           // boottime ns (audio anchor + start)
  t_end_ns: number;
  kind?: "wake" | "narration";
  subtask?: SubtaskTag;
  wake_boundary?: number | null;
  wake_offset_ms?: number;
}

export interface TranscriptV2 {
  schema: 2;
  engine: string;
  model: string;
  language: string;
  created_at_ms: number;
  clock: string;
  audio_start_elapsed_ns: number;
  text: string;
  segments: TxSegment[];
  warnings?: string[];
}

export const TX_CHUNK_SECONDS = 30;
export const TX_RETRY_MODEL = "gemini-2.5-flash";
/** Slop allowed before a segment counts as "past the end of its chunk". */
export const OUT_OF_RANGE_TOL_S = 0.35;

// ── task_events.jsonl → sub-task windows ────────────────────────────────────
// Pairs subtask_start/subtask_done by index; an unclosed window (aborted run)
// closes at the last-seen timestamp and is flagged aborted.
export function parseTaskEvents(text: string): { segs: TaskWindow[]; done: boolean } {
  const open = new Map<number, TaskWindow>();
  const segs: TaskWindow[] = [];
  let done = false, lastNs = 0;
  for (const line of text.split("\n")) {
    if (!line.trim()) continue;
    let e: any;
    try { e = JSON.parse(line); } catch { continue; }
    if (typeof e.t_ns !== "number") continue;
    lastNs = e.t_ns;
    if (e.event === "subtask_start") {
      open.set(e.index, { index: e.index, text: e.text || `Step ${e.index + 1}`, t_start_ns: e.t_ns, t_end_ns: 0 });
    } else if (e.event === "subtask_done") {
      const s = open.get(e.index);
      if (s) { s.t_end_ns = e.t_ns; segs.push(s); open.delete(e.index); }
    } else if (e.event === "task_done") done = true;
  }
  for (const s of open.values()) { s.t_end_ns = lastNs; s.aborted = true; segs.push(s); }
  segs.sort((a, b) => a.t_start_ns - b.t_start_ns);
  return { segs, done };
}

// ── deterministic tagging ───────────────────────────────────────────────────
/** True when the text is (only) the spoken step-advance command ("Next step."). */
export function isWakePhrase(text: string): boolean {
  const norm = (text || "").toLowerCase().replace(/[^a-z]+/g, " ").trim();
  return /^(?:(?:uh|um|ah|okay|ok|and) )?(?:next|nex|text) ?(?:step|steps|up)(?: please)?$/.test(norm);
}

/** Which sub-task window a segment belongs to: most-overlap, else intro/outro/nearest. */
export function assignSubtask(seg: TxSegment, windows: TaskWindow[]): SubtaskTag {
  if (!windows?.length) return "intro";
  let best = -1, bestOv = 0;
  for (const w of windows) {
    const ov = Math.min(seg.t_end_ns, w.t_end_ns) - Math.max(seg.t_start_ns, w.t_start_ns);
    if (ov > bestOv) { bestOv = ov; best = w.index; }
  }
  if (best >= 0) return best;
  const mid = (seg.t_start_ns + seg.t_end_ns) / 2;
  if (mid < windows[0].t_start_ns) return "intro";
  if (mid >= windows[windows.length - 1].t_end_ns) return "outro";
  let nearest = windows[0], nd = Infinity;
  for (const w of windows) {
    const d = Math.abs(mid - (w.t_start_ns + w.t_end_ns) / 2);
    if (d < nd) { nd = d; nearest = w; }
  }
  return nearest.index;
}

/**
 * Stamp wake/narration + sub-task + wake→boundary attribution onto a segment
 * list, in place. Skips already-tagged segments (schema-2 files keep theirs).
 * Returns the same array for chaining.
 */
export function tagSegments(segs: TxSegment[], windows: TaskWindow[]): TxSegment[] {
  if (!windows?.length) return segs;
  for (const s of segs) {
    if (s.kind) continue;
    s.kind = isWakePhrase(s.text) ? "wake" : "narration";
    s.subtask = assignSubtask(s, windows);
    if (s.kind === "wake") {
      let boundary: number | null = null, off = Infinity;
      for (const w of windows) {
        const d = (w.t_end_ns - s.t_end_ns) / 1e6;
        if (d >= -2000 && d <= 2000 && Math.abs(d) < Math.abs(off)) { boundary = w.index; off = d; }
      }
      s.wake_boundary = boundary;
      if (boundary != null) s.wake_offset_ms = Math.round(off);
    }
  }
  return segs;
}

// ── chunking ────────────────────────────────────────────────────────────────
/**
 * Cut points (seconds) that keep every chunk ≤ maxChunkSeconds AND never let a
 * chunk straddle a sub-task boundary — so a segment can't span two steps. Plain
 * sessions (no windows) fall back to equal ≤max pieces.
 */
export function computeCutTimes(
  windows: TaskWindow[], audioStartNs: number, durationSec: number, maxChunkSeconds: number,
): number[] {
  const boundaries = (windows || [])
    .map((w) => (w.t_end_ns - audioStartNs) / 1e9)
    .filter((s) => s > 0 && s < durationSec)
    .sort((a, b) => a - b);
  const cuts: number[] = [];
  let prev = 0;
  const pushSpan = (end: number, includeEnd: boolean) => {
    const n = Math.max(1, Math.ceil((end - prev) / maxChunkSeconds));
    for (let i = 1; i < n; i++) cuts.push(prev + ((end - prev) * i) / n);
    if (includeEnd) cuts.push(end);
    prev = end;
  };
  for (const b of boundaries) {
    if (b - prev < 0.25) continue;
    pushSpan(b, true);
  }
  if (durationSec - prev > 0) pushSpan(durationSec, false);
  return cuts.map((c) => Math.round(c * 1000) / 1000);
}

/**
 * Task-aware STT prompt for one chunk: restates the base instruction (this
 * REPLACES the worker default), names the task + the step(s) active in the
 * chunk, and pins the "Next step" command phrasing.
 */
export function buildChunkPrompt(
  taskName: string | undefined, windows: TaskWindow[], audioStartNs: number, fromSec: number, toSec: number,
): string {
  const base =
    "This is an audio recording of a person narrating a manipulation task they are physically performing, " +
    "for robotics training data. Transcribe their speech verbatim. Break it into short segments — roughly one " +
    "sentence or one spoken subtask/step per segment. For each segment return its start and end time in seconds " +
    "(floating point) measured from the very beginning of the audio, and the exact spoken text. " +
    "Omit silence, breaths, and non-speech sounds — return only segments that contain speech. " +
    "If there is no speech at all, return an empty array.";
  const active = windows.filter(
    (w) => (w.t_start_ns - audioStartNs) / 1e9 < toSec && (w.t_end_ns - audioStartNs) / 1e9 > fromSec);
  let ctx = taskName ? ` The speaker is performing the task: "${taskName}".` : "";
  if (active.length) {
    const steps = active.map((w) => `"${w.text}"`).join(", then ");
    ctx += ` During this clip they are working on the step${active.length > 1 ? "s" : ""}: ${steps}.`;
  }
  ctx += ' They say the exact phrase "Next step" (by itself) as a voice command to advance to the next step — ' +
         "transcribe each such utterance verbatim as its own separate segment.";
  return base + ctx;
}

/**
 * Timestamp-compression detector: lots of text whose claimed span is a small
 * fraction of the chunk at an implausible words/sec. Speech runs ~2–3 w/s; a
 * squished chunk reads ~10×. (Gemini occasionally compresses long-audio times.)
 */
export function looksCompressed(segments: RawSeg[], chunkDurSec: number): boolean {
  const spoken = (segments || []).filter((s) => s && typeof s.text === "string" && s.text.trim());
  if (!spoken.length) return false;
  const words = spoken.reduce((n, s) => n + s.text.trim().split(/\s+/).length, 0);
  if (words < 20) return false;
  const span = Math.max(...spoken.map((s) => Number(s.end) || 0)) - Math.min(...spoken.map((s) => Number(s.start) || 0));
  return span < chunkDurSec * 0.5 && words / Math.max(span, 0.1) > 6;
}

/** Assemble the schema-2 transcript.json object from merged whole-audio segments. */
export function assembleTranscript(
  merged: RawSeg[], windows: TaskWindow[],
  opts: { model: string; audioStartNs: number; warnings?: string[]; nowMs: number },
): TranscriptV2 {
  const clean = merged
    .filter((s) => s && typeof s.text === "string" && s.text.trim().length)
    .map((s) => ({ start: s.start, end: s.end, text: s.text.trim() }))
    .sort((a, b) => a.start - b.start);
  const tx: TranscriptV2 = {
    schema: 2, engine: "gemini", model: opts.model, language: "en",
    created_at_ms: opts.nowMs, clock: "SystemClock.elapsedRealtimeNanos",
    audio_start_elapsed_ns: opts.audioStartNs,
    text: clean.map((s) => s.text).join(" "),
    segments: clean.map((s) => ({
      ...s,
      t_start_ns: Math.round(opts.audioStartNs + s.start * 1e9),
      t_end_ns: Math.round(opts.audioStartNs + s.end * 1e9),
    })),
    ...(opts.warnings?.length ? { warnings: opts.warnings } : {}),
  };
  tagSegments(tx.segments, windows);
  return tx;
}

// ── WAV decode / decimate / (re)encode ──────────────────────────────────────
// Pure ArrayBuffer↔Float32Array; replaces ffmpeg. Handles PCM 16/24/32-bit and
// float32, any channel count → mono.
export function wavToMono(buf: ArrayBuffer): { rate: number; samples: Float32Array } {
  const dv = new DataView(buf);
  const tag = (o: number) => String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));
  if (tag(0) !== "RIFF" || tag(8) !== "WAVE") throw new Error("audio.wav is not a RIFF/WAVE file");
  let fmt: { code: number; ch: number; rate: number; bits: number } | null = null;
  let dataOff = -1, dataLen = 0;
  for (let o = 12; o + 8 <= dv.byteLength;) {
    const id = tag(o), size = dv.getUint32(o + 4, true);
    if (id === "fmt ") fmt = { code: dv.getUint16(o + 8, true), ch: dv.getUint16(o + 10, true),
                               rate: dv.getUint32(o + 12, true), bits: dv.getUint16(o + 22, true) };
    else if (id === "data") { dataOff = o + 8; dataLen = Math.min(size, dv.byteLength - dataOff); }
    o += 8 + size + (size & 1);
  }
  if (!fmt || dataOff < 0) throw new Error("audio.wav has no fmt/data chunks");
  const bytes = fmt.bits / 8, frames = Math.floor(dataLen / (bytes * fmt.ch));
  const read =
      (fmt.code === 3 && fmt.bits === 32) ? (p: number) => dv.getFloat32(p, true)
    : (fmt.bits === 16) ? (p: number) => dv.getInt16(p, true) / 0x8000
    : (fmt.bits === 32) ? (p: number) => dv.getInt32(p, true) / 0x80000000
    : (fmt.bits === 24) ? (p: number) => ((dv.getUint8(p) | (dv.getUint8(p + 1) << 8) | (dv.getInt8(p + 2) << 16)) / 0x800000)
    : null;
  if (!read) throw new Error(`unsupported WAV format (code ${fmt.code}, ${fmt.bits}-bit)`);
  const mono = new Float32Array(frames);
  for (let i = 0; i < frames; i++) {
    let s = 0;
    for (let c = 0; c < fmt.ch; c++) s += read(dataOff + (i * fmt.ch + c) * bytes);
    mono[i] = s / fmt.ch;
  }
  return { rate: fmt.rate, samples: mono };
}

/** Box-average decimation to 16 kHz — plenty for speech, ~10× smaller payloads. */
export function downsampleTo16k(samples: Float32Array, rate: number): { rate: number; samples: Float32Array } {
  if (rate <= 16000) return { rate, samples };
  const ratio = rate / 16000, n = Math.floor(samples.length / ratio);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const a = Math.floor(i * ratio), b = Math.min(samples.length, Math.max(a + 1, Math.floor((i + 1) * ratio)));
    let s = 0;
    for (let j = a; j < b; j++) s += samples[j];
    out[i] = s / (b - a);
  }
  return { rate: 16000, samples: out };
}

/** [from,to) of a mono float buffer → a 16-bit PCM WAV file as raw bytes. */
export function pcm16Wav(samples: Float32Array, rate: number, from: number, to: number): Uint8Array {
  const n = to - from;
  const buf = new ArrayBuffer(44 + n * 2), dv = new DataView(buf);
  const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); };
  w(0, "RIFF"); dv.setUint32(4, 36 + n * 2, true); w(8, "WAVE");
  w(12, "fmt "); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, rate, true); dv.setUint32(28, rate * 2, true); dv.setUint16(32, 2, true); dv.setUint16(34, 16, true);
  w(36, "data"); dv.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const v = Math.max(-1, Math.min(1, samples[from + i]));
    dv.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Uint8Array(buf);
}

// ── orchestrator ─────────────────────────────────────────────────────────────
export interface TranscribeInput {
  audioBuffer: ArrayBuffer;          // audio.wav bytes
  taskEventsText?: string | null;    // task_events.jsonl contents (null → plain session)
  audioStartNs: number;              // meta clock anchor for audio
  taskName?: string;
  chunkSeconds?: number;             // default TX_CHUNK_SECONDS
  nowMs?: number;                    // for deterministic created_at_ms in tests
}

export interface TranscribeDeps {
  // The one impure step: hand a chunk's WAV bytes to Gemini and get segments
  // back (times relative to the chunk). The caller owns fetch/auth/base64.
  transcribeChunk: (wav: Uint8Array, prompt: string | undefined, model: string | undefined)
    => Promise<{ segments: RawSeg[]; model: string }>;
  onProgress?: (windowIndex: number, windowCount: number) => void;
}

/**
 * Full offline transcribe: decode audio → cut at sub-task boundaries → ask the
 * injected transcriber per chunk (retrying a timestamp-compressed chunk once on
 * the fallback model) → assemble + deterministically tag the schema-2
 * transcript.json object. Also returns the parsed task windows so a caller can
 * reuse them without re-parsing.
 */
export async function transcribeSession(
  input: TranscribeInput, deps: TranscribeDeps,
): Promise<{ transcript: TranscriptV2; windows: TaskWindow[]; done: boolean }> {
  const maxChunk = input.chunkSeconds ?? TX_CHUNK_SECONDS;
  const nowMs = input.nowMs ?? Date.now();
  const parsed = input.taskEventsText ? parseTaskEvents(input.taskEventsText) : { segs: [], done: false };
  const windows = parsed.segs;

  const raw = wavToMono(input.audioBuffer);
  const { rate, samples } = downsampleTo16k(raw.samples, raw.rate);
  const anchor = input.audioStartNs;
  const durationSec = samples.length / rate;

  const cuts = computeCutTimes(windows, anchor, durationSec, maxChunk);
  const offsets = [0, ...cuts];
  const merged: RawSeg[] = [];
  const warnings: string[] = [];
  let model = "";

  for (let c = 0; c < offsets.length; c++) {
    deps.onProgress?.(c, offsets.length);
    const fromSec = offsets[c], toSec = offsets[c + 1] ?? durationSec;
    const from = Math.round(fromSec * rate), to = Math.min(samples.length, Math.round(toSec * rate));
    if (to <= from) continue;
    const prompt = windows.length ? buildChunkPrompt(input.taskName, windows, anchor, fromSec, toSec) : undefined;

    let r = await deps.transcribeChunk(pcm16Wav(samples, rate, from, to), prompt, undefined);
    // Timestamp-compression guard: retry a squished chunk once on the reliable
    // fallback model; keep the retry only if it actually looks sane.
    if (looksCompressed(r.segments, toSec - fromSec)) {
      try {
        const retry = await deps.transcribeChunk(pcm16Wav(samples, rate, from, to), prompt, TX_RETRY_MODEL);
        if (!looksCompressed(retry.segments, toSec - fromSec)) r = retry;
        else warnings.push(`compressed_timestamps: chunk ${fromSec.toFixed(1)}–${toSec.toFixed(1)}s came back timestamp-compressed twice — times in this span are unreliable`);
      } catch (e: any) {
        warnings.push(`compressed_timestamps: chunk ${fromSec.toFixed(1)}–${toSec.toFixed(1)}s looks timestamp-compressed and the retry failed (${e?.message || e})`);
      }
    }
    if (!model && r.model) model = r.model;
    // Out-of-range guard: a segment cannot legally start after the end of the
    // chunk it was transcribed from. Gemini sometimes keeps "narrating" past
    // the audio (inventing several seconds of speech at the tail), which would
    // otherwise land as phantom segments beyond the end of the recording.
    // Code owns time: drop what can't exist, clamp what merely overhangs.
    const chunkDur = toSec - fromSec;
    let dropped = 0;
    for (const s of r.segments) {
      let st = Number(s.start) || 0;
      let en = Number(s.end) || 0;
      if (st < 0) st = 0;
      if (st > chunkDur + OUT_OF_RANGE_TOL_S) { dropped++; continue; }
      if (en > chunkDur) en = chunkDur;
      if (en <= st) en = Math.min(chunkDur, st + 0.3);
      // A segment that starts at the very end clamps to nothing — that's an
      // invention too, not speech we can place.
      if (en - st < 0.05) { dropped++; continue; }
      merged.push({ start: st + fromSec, end: en + fromSec, text: s.text });
    }
    if (dropped) {
      warnings.push(`out_of_range: dropped ${dropped} segment(s) the model placed past the end of the ` +
        `${fromSec.toFixed(1)}–${toSec.toFixed(1)}s chunk (invented speech beyond the audio)`);
    }
  }

  const transcript = assembleTranscript(merged, windows, { model, audioStartNs: anchor, warnings, nowMs });
  return { transcript, windows, done: parsed.done };
}
